import type { CrmLeadCardInFunnelData } from '@/api/types/crm/crmFunnels.types'
import { extractVkChatMetaFromPayload } from '../../../utils/leadVkChatMeta'
import {
  toValidFunnelId,
  type LeadCardApiData,
  type LeadCardCurrentFunnel,
} from '../leadModalRefetchMerge'
import type { LeadProfileLead } from '../LeadProfileModal.types'

export function deriveSelectedFunnelIdFromLead(
  lead: LeadProfileLead | undefined,
): number | null {
  if (!lead) return null

  const currentFunnelId = toValidFunnelId(lead.currentFunnelId)
  if (currentFunnelId !== null) return currentFunnelId

  const rawFunnels = Array.isArray(lead.funnels)
    ? (lead.funnels as Array<{
        id?: number | string
        isPrimary?: boolean
        is_primary?: boolean
      }>)
    : []
  const primaryFunnel = rawFunnels.find(
    (funnel) => funnel?.isPrimary || funnel?.is_primary,
  )
  return toValidFunnelId(primaryFunnel?.id)
}

export const parsePositiveId = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null
  const n = Number(value)
  return Number.isFinite(n) && n > 0 ? n : null
}

export const getLeadFunnelRowId = (funnel: {
  id: number
  lead_funnel_id?: number
}): number => {
  const raw = funnel.lead_funnel_id ?? funnel.id
  const id = Number(raw)
  return Number.isFinite(id) && id > 0 ? id : Number(funnel.id)
}

export const isCrmSettingEnabled = (raw: unknown): boolean => {
  if (raw === 1 || raw === true || raw === '1') {
    return true
  }
  if (typeof raw !== 'string') {
    return false
  }
  try {
    const parsed = JSON.parse(raw)
    return parsed === 1 || parsed === true || parsed === '1'
  } catch {
    return false
  }
}

export const extractVkMetaFromGetLeadResponse = (
  response: { data?: { data?: unknown; status?: string } } | undefined,
): unknown => {
  const payload = response?.data?.data
  return extractVkChatMetaFromPayload(payload)
}

const getCardRowForFunnel = (
  card: LeadCardApiData | null,
  funnelId: number,
  leadFunnelId?: number | null,
): LeadCardCurrentFunnel | undefined => {
  const list = card?.current_funnels
  if (!Array.isArray(list)) return undefined

  if (
    leadFunnelId != null &&
    Number.isFinite(Number(leadFunnelId)) &&
    Number(leadFunnelId) > 0
  ) {
    const byDeal = list.find(
      (cf) => Number(cf.lead_funnel_id ?? cf.id) === Number(leadFunnelId),
    )
    if (byDeal) return byDeal
  }

  return list.find((cf) => Number(cf.funnel_id) === funnelId)
}

const getCurrentStatusIdFromCardFunnel = (
  data: CrmLeadCardInFunnelData | undefined,
): number | null => {
  if (!data) return null
  const cur = data.current_status ?? data.status
  return parsePositiveId(cur?.id)
}

/** Один раз вытащить из снимков поля по выбранной воронке: сначала `card-funnel`, иначе строка из `card`. */
export function deriveCommittedFunnelSlice(
  selectedFunnelId: number | null,
  cardFunnelSnapshots: Partial<Record<number, CrmLeadCardInFunnelData>>,
  leadCardSnapshot: LeadCardApiData | null,
  selectedLeadFunnelId?: number | null,
): {
  committedManagerId: number | null
  committedNextContactAt: string | null
  committedCurrentStatusId: number | null
  committedComment: string | null
  committedEnteredAt: string | null
} {
  const empty = {
    committedManagerId: null as number | null,
    committedNextContactAt: null as string | null,
    committedCurrentStatusId: null as number | null,
    committedComment: null as string | null,
    committedEnteredAt: null as string | null,
  }
  if (
    selectedFunnelId === null ||
    !Number.isFinite(Number(selectedFunnelId))
  ) {
    return empty
  }
  const fid = Number(selectedFunnelId)
  const deepRaw = cardFunnelSnapshots[fid]
  const dealId =
    selectedLeadFunnelId != null &&
    Number.isFinite(Number(selectedLeadFunnelId)) &&
    Number(selectedLeadFunnelId) > 0
      ? Number(selectedLeadFunnelId)
      : null

  let deep = deepRaw
  if (deep && dealId != null) {
    const snapDealId = Number(deep.lead_funnel_id)
    if (!Number.isFinite(snapDealId) || snapDealId <= 0 || snapDealId !== dealId) {
      deep = undefined
    }
  }

  const cardRow =
    deep === undefined
      ? getCardRowForFunnel(leadCardSnapshot, fid, dealId)
      : undefined

  const committedManagerId =
    deep !== undefined
      ? parsePositiveId(deep.manager?.id)
      : parsePositiveId(cardRow?.manager_id)

  let committedNextContactAt: string | null = null
  if (deep !== undefined) {
    const raw = deep.next_contact_at
    if (raw != null && String(raw).trim() !== '') committedNextContactAt = String(raw)
  } else {
    const raw = cardRow?.next_contact_at
    if (raw != null && String(raw).trim() !== '') committedNextContactAt = String(raw)
  }

  const committedCurrentStatusId =
    deep !== undefined
      ? getCurrentStatusIdFromCardFunnel(deep)
      : parsePositiveId(cardRow?.current_status_id)

  const rawComment =
    deep !== undefined ? deep.comment : cardRow?.comment
  const committedComment =
    rawComment != null && String(rawComment).trim() !== ''
      ? String(rawComment)
      : null

  const rawEnteredAt =
    deep !== undefined ? deep.entered_at : cardRow?.entered_at
  const committedEnteredAt =
    rawEnteredAt != null && String(rawEnteredAt).trim() !== ''
      ? String(rawEnteredAt)
      : null

  return {
    committedManagerId,
    committedNextContactAt,
    committedCurrentStatusId,
    committedComment,
    committedEnteredAt,
  }
}
